import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import logo from "../../assets/zeos-ai-blue-logo.png";
import { zeosDialog } from "../../components/Shared/confirm-dialog/ConfirmDialog";
import { ZeosPasswordInput } from "../../components/Shared/ZeosPasswordInput";
import { APP_VERSION } from "../../configs/AppVersion";
import { AuthContext } from "../../context/AuthContext";
import { useFormWithSchema } from "../../hooks/useFormWithSchema";
import { useSignIn } from "../../hooks/useSignIn";
import { loginValidationSchema } from "../../services/forms/validationSchemas/LoginValidationSchema";
import { ZeosButton } from "./ZeosButton";
import { ZeosInput } from "./ZeosInput";

interface ILoginForm {
  username: string;
  password: string;
}

interface IZeosSigninProps {
  buttonLabel?: string;
  buttonClassName?: string;
  containerClassName?: string;
  defaultPassword?: string;
  defaultUsername?: string;
  enableIdleTimeout?: boolean;
  logoSrc?: string;
  mode?: "page" | "modal";
  onCancel?: () => void;
  onFailure?: () => void;
  onSuccess?: (user: any) => void;
  resetLinkSource?: "onboarding" | "dashboard";
  showFooter?: boolean;
  showLogo?: boolean;
  showResetLink?: boolean;
}

export const ZeosSignin = ({
  buttonLabel = "Login",
  buttonClassName,
  containerClassName,
  defaultPassword = "",
  defaultUsername = "",
  enableIdleTimeout = true,
  logoSrc,
  mode = "page",
  onCancel,
  onFailure,
  onSuccess,
  resetLinkSource = "dashboard",
  showFooter = true,
  showLogo = true,
  showResetLink = true,
}: IZeosSigninProps) => {
  const isModal = mode === "modal";

  const authContext = useContext(AuthContext);
  const navigate = useNavigate();
  const { signOut } = useSignIn();
  const timeoutIdRef = useRef<NodeJS.Timeout | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>("");

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useFormWithSchema(loginValidationSchema);

  useEffect(() => {
    setValue("username", defaultUsername);
    setValue("password", defaultPassword);
  }, [defaultUsername, defaultPassword, setValue]);

  //TODO: DOES THIS NEED TO BE IN APP.TSX?
  useEffect(() => {
    if (!enableIdleTimeout) return;
    const events = [
      "mousedown",
      "mousemove",
      "keypress",
      "scroll",
      "touchstart",
    ];
    const resetTimer = () => resetTimeout();
    events.forEach((event) => window.addEventListener(event, resetTimer));
    return () => {
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }
      events.forEach((event) => window.removeEventListener(event, resetTimer));
    };
  }, [enableIdleTimeout]);

  const resetTimeout = () => {
    if (timeoutIdRef.current) clearTimeout(timeoutIdRef.current);
    timeoutIdRef.current = setTimeout(() => {
      signOut(() => navigate("/login"));
    }, 24 * 60 * 60 * 1000);
  };

  const { signIn } = useSignIn();

  const handleLogin = async (email: string, password: string) => {
    if (enableIdleTimeout) resetTimeout();

    await signIn({
      email,
      password,
      onSuccess: async (user) => {
        onSuccess?.(user);
      },
      onFailure: async () => {
        setErrorMessage("Invalid email or password");
        onFailure?.();
      },
    });
  };

  const onSubmit = async (data: ILoginForm) => {
    await handleLogin(data.username, data.password);
  };

  const handleResetLink = async () => {
    const email = watch("username")?.trim();
    if (!email) {
      zeosDialog("Please enter your email address to reset your password.");
      return;
    }
    try {
      await authContext.passwordResetAsync(email);
    } catch (err) {
      // Firebase intentionally does not confirm/deny account existence; log only.
      console.log("passwordReset", err);
    }
    // Same message whether or not the account exists (avoids account enumeration).
    zeosDialog(
      "If an account exists for that email, a password reset link has been sent. Please check your inbox."
    );
  };

  const form = (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div
        className={
          isModal
            ? "grid grid-cols-12 gap-4 w-full"
            : "w-full max-w-3xl mx-auto px-4 flex flex-col gap-6 mt-8"
        }
      >
        <div
          className={isModal ? "col-span-12 sm:col-span-6" : "w-full max-w-md"}
        >
          <ZeosInput
            label="Email"
            placeholder="Enter your email"
            value={watch("username")}
            onChange={(e) => setValue("username", e.target.value)}
            error={errors.username?.message}
            name={""}
          />
        </div>
        <div
          className={isModal ? "col-span-12 sm:col-span-6" : "w-full max-w-md"}
        >
          <ZeosPasswordInput
            name="password"
            label="Password"
            placeholder="Enter password"
            value={watch("password")}
            onChange={(e) => setValue("password", e.target.value)}
            error={errors.password?.message}
          />
        </div>

        {errorMessage && (
          <div className={isModal ? "col-span-12" : "w-full max-w-md"}>
            <p className="text-zeos-red text-xs text-center mt-1">
              {errorMessage}
            </p>
          </div>
        )}

        <div
          className={`col-span-12 flex gap-2 mt-4 ${
            isModal ? "justify-end" : "justify-center"
          }`}
        >
          <ZeosButton
            type="submit"
            label={buttonLabel}
            mode="action"
            className={buttonClassName}
          />
          {isModal && (
            <ZeosButton
              type="button"
              label="Cancel"
              mode="prev"
              onPress={() => onCancel?.()}
            />
          )}
        </div>
      </div>
    </form>
  );
  const wrapperClass = isModal
    ? containerClassName ?? ""
    : containerClassName ??
      "flex flex-col justify-between h-screen border-l-4 border-zeos-orange";

  return (
    <div className={wrapperClass}>
      {showLogo && !isModal && (
        <div className="w-full flex justify-center items-center pt-16">
          <img
            className="w-[250px]"
            src={logoSrc ?? logo}
            alt="ZEOS-AI logo"
          />
        </div>
      )}

      <div className="flex justify-center">
        <div className="mx-auto w-full">{form}</div>
      </div>

      {showResetLink && (
        <div className="flex justify-center mt-4">
          <span
            className="text-xs underline cursor-pointer"
            onClick={handleResetLink}
          >
            Forgot your password?
          </span>
        </div>
      )}
      {showFooter && !isModal && (
        <div className="flex justify-between px-2 mt-auto">
          <div className="flex">
            <p className="pr-1 text-xs">Terms of Use</p>
            <p className="pl-1 text-xs">Privacy Policy</p>
          </div>
          <p className="text-xs">ZEOS-AI {APP_VERSION}</p>
        </div>
      )}
    </div>
  );
};
